import type { Edge, Node } from '@xyflow/react';
import { LAYOUT } from './constants';
import { NodeType, StaticNodeType } from '../hooks/use-sidebar-control.types';

const makeId = (prefix: string) => `${prefix}-${crypto.randomUUID()}`;

export const recalculateLinearPosition = (nodes: Node[]) => {
  const sortedNodes = [...nodes].sort((a, b) => a.position.x - b.position.x);
  const defaultNodeWidth = 320;
  const defaultHorizontalMargin = 50;
  let currentX = 200;

  return sortedNodes.map((node) => {
    const updatedNode = {
      ...node,
      position: { x: currentX, y: 200 },
    };
    currentX += defaultNodeWidth + defaultHorizontalMargin;
    return updatedNode;
  });
};

export function insertNodeAtPlaceholder(params: {
  nodes: Node[];
  edges: Edge[];
  placeholderId: string;
  nodeType: NodeType;
  data?: Record<string, unknown>;
}): { nodes: Node[]; edges: Edge[] } {
  const { nodes, edges, placeholderId, nodeType, data } = params;

  // New node can only be added if at least one enroll node exists already.
  if (nodeType === NodeType.Wait) {
    const hasCanvasEnrollNode = nodes.some((n) => n.type === NodeType.Enroll);
    if (!hasCanvasEnrollNode) {
      return { nodes, edges };
    }
  }

  const placeholder = nodes.find((n) => n.id === placeholderId);
  if (!placeholder) {
    return { nodes, edges };
  }

  const incoming = edges.find((e) => e.target === placeholderId);
  if (!incoming) {
    return { nodes, edges };
  }

  const newNodeId = makeId(nodeType);
  const newAddId = makeId('add');

  const newNodeX = placeholder.position.x;
  const newNodeY = placeholder.position.y;

  const newNode: Node = {
    id: newNodeId,
    type: nodeType,
    position: { x: newNodeX, y: newNodeY },
    data: { ...data },
  };

  // Create new Add node to the right of the inserted node
  const newAddNode: Node = {
    id: newAddId,
    type: StaticNodeType.Add,
    position: { x: newNodeX + LAYOUT.enrollWidth + LAYOUT.gapX, y: newNodeY },

    data: {},
  };

  const updatedIncoming: Edge = { ...incoming, target: newNodeId };

  const nextEdges: Edge[] = [
    ...edges.filter((e) => e.id !== incoming.id),
    updatedIncoming,
    { id: makeId('e'), source: newNodeId, target: newAddId, type: 'smoothstep' },
  ];

  const nextNodes: Node[] = [...nodes.filter((n) => n.id !== placeholderId), newNode, newAddNode];

  return { nodes: nextNodes, edges: nextEdges };
}

// When a node is removed from a chain, reconnect to its neighbors
export function healEdgesAfterNodeRemoval(params: {
  edges: Edge[];
  removedNodeIds: string[];
}): Edge[] {
  const { edges, removedNodeIds } = params;

  const removedIds = new Set(removedNodeIds);

  let nextEdges = edges;

  for (const removedId of removedIds) {
    const incoming = nextEdges.find((e) => e.target === removedId);
    const outgoing = nextEdges.find((e) => e.source === removedId);

    nextEdges = nextEdges.filter((e) => e.source !== removedId && e.target !== removedId);

    if (incoming && outgoing) {
      const exists = nextEdges.some(
        (e) => e.source === incoming.source && e.target === outgoing.target
      );

      if (!exists) {
        nextEdges = [
          ...nextEdges,
          {
            id: makeId('e'),
            source: incoming.source,
            target: outgoing.target,
            type: incoming.type ?? 'smoothstep',
          },
        ];
      }
    }
  }

  return nextEdges;
}

// Decides whether the graph should be reset to a single Start node.
export function shouldResetToStart(nodes: Node[]): boolean {
  if (nodes.length === 0) {
    return true;
  }
  const hasRealNodes = nodes.some((n) => n.type !== 'add');
  return !hasRealNodes;
}
